import { useCallback, useEffect, useReducer, useMemo } from 'react';
import useWindowSize from './useWindowsSize';

import {
  getBestPriceByAirline,
  getCustomAirline,
} from '@shared/helpers/flights/flights';

import {
  getScalesBySearchType,
  scalesFilter,
} from '@shared/helpers/flights/filters/scales/filter';

import {
  baggageFilter,
  getBaggageBySearchType,
} from '@shared/helpers/flights/filters/baggage/filter';

import { carriersFilter } from '@shared/helpers/flights/filters/carrier/filter';

import {
  getPriceBySearchType,
  priceFilter,
} from '@shared/helpers/flights/filters/price/filter';

import {
  durationFilter,
  getDurationBySearchType,
} from '@shared/helpers/flights/filters/duration/filter';

const reducerMap = {
  APPLY: (state, action) => ({
    ...state,
    ...action.payload,
    isLoading: false,
  }),

  SET_LOADING: (state, action) => ({
    ...state,
    isLoading: action.value,
  }),

  CLEAR: (_, action) => action.payload,
};

const reducer = (state, action) =>
  reducerMap[action.type]?.(state, action) ?? state;

const useFlightFilters = ({
  initialFilters,
  optimizedClusters,
  flightResultFiltered,
  setFlightResultFiltered,
  optimizedCustomClusters,
  flightsCustomDta,
  isCustomSearch,
  setNoResultsInTheFilter,
  setIsFiltering,
}) => {
  const screenSize = useWindowSize();

  const initialFlightResult = useMemo(
    () => (isCustomSearch ? optimizedCustomClusters : optimizedClusters),
    [isCustomSearch, optimizedCustomClusters, optimizedClusters]
  );

  const getAirline = isCustomSearch ? getCustomAirline : getBestPriceByAirline;

  const getScales = useMemo(() => getScalesBySearchType(isCustomSearch), [
    isCustomSearch,
  ]);

  const getBaggage = useMemo(() => getBaggageBySearchType(isCustomSearch), [
    isCustomSearch,
  ]);

  const getMinMaxPrice = useMemo(() => getPriceBySearchType(isCustomSearch), [
    isCustomSearch,
  ]);

  const getMinMaxDuration = useMemo(
    () => getDurationBySearchType(isCustomSearch),
    [isCustomSearch]
  );

  const initialState = {
    selections: {
      baggage: 'backpack_baggage',
      scale: 'allOptions',
      carrier: ['allOptions'],
      price: initialFilters.max_price,
      duration: initialFilters.max_duration,
    },
    ranges: {
      price: [initialFilters.min_price, initialFilters.max_price],
      duration: [initialFilters.min_duration, initialFilters.max_duration],
    },
    available: {
      baggage: initialFilters.baggage,
      scales: initialFilters.scales,
      airlines: getAirline(
        isCustomSearch ? flightsCustomDta : flightResultFiltered
      ),
    },
    filteredFlights: initialFlightResult,
    isLoading: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const newState = {
      selections: {
        baggage: 'backpack_baggage',
        scale: 'allOptions',
        carrier: ['allOptions'],
        price: initialFilters.max_price,
        duration: initialFilters.max_duration,
      },
      ranges: {
        price: [initialFilters.min_price, initialFilters.max_price],
        duration: [initialFilters.min_duration, initialFilters.max_duration],
      },
      available: {
        baggage: initialFilters.baggage,
        scales: initialFilters.scales,
        airlines: getAirline(
          isCustomSearch ? flightsCustomDta : flightResultFiltered
        ),
      },
      filteredFlights: initialFlightResult,
      isLoading: false,
    };
    dispatch({ type: 'CLEAR', payload: newState });
    setFlightResultFiltered(initialFlightResult);
    setNoResultsInTheFilter(false);
  }, [isCustomSearch, initialFlightResult]);

  const FILTER_APPLIERS = {
    baggage: (res, v) => baggageFilter(res, v, isCustomSearch),
    scale: (res, v) => scalesFilter(res, v, isCustomSearch),
    carrier: (res, v) => carriersFilter(res, v, isCustomSearch),
    price: (res, v) => priceFilter(res, v, isCustomSearch),
    duration: (res, v) =>
      durationFilter(res, v, isCustomSearch, state.ranges.duration?.[0]),
  };

  const isDefaultSelection = (key, value) => {
    if (key === 'carrier') {
      return value.includes('allOptions');
    }
    return value === initialState.selections[key];
  };

  const applyAllFilters = selections =>
    Object.entries(selections).reduce((acc, [key, value]) => {
      if (isDefaultSelection(key, value)) return acc;
      return FILTER_APPLIERS[key](acc, value);
    }, initialFlightResult);

  const applyFiltersExcept = (excludeKey, selections) =>
    Object.entries(selections).reduce((acc, [key, value]) => {
      if (key === excludeKey || isDefaultSelection(key, value)) return acc;
      return FILTER_APPLIERS[key](acc, value);
    }, initialFlightResult);

  const applyFilters = useCallback(
    selections => {
      setIsFiltering(true);
      if (screenSize.width > 600)
        dispatch({ type: 'SET_LOADING', value: true });

      const filteredFlights = applyAllFilters(selections);

      if (filteredFlights.length === 0) {
        setNoResultsInTheFilter(true);
        setFlightResultFiltered(initialFlightResult);
        return;
      }

      setNoResultsInTheFilter(false);

      const isPriceChanged = selections.price !== state.selections.price;
      const isDurationChanged =
        selections.duration !== state.selections.duration;

      // recalcular rangos solo si se filtró por otra cosa
      const newPriceRange = isPriceChanged
        ? state.ranges.price
        : getMinMaxPrice(filteredFlights);

      const newDurationRange = isDurationChanged
        ? state.ranges.duration
        : getMinMaxDuration(filteredFlights);

      dispatch({
        type: 'APPLY',
        payload: {
          selections,
          filteredFlights,
          ranges: {
            price: newPriceRange,
            duration: newDurationRange,
          },
          available: {
            baggage: getBaggage(
              initialFilters.baggage,
              applyFiltersExcept('baggage', selections)
            ),
            scales: getScales(applyFiltersExcept('scale', selections)),
            airlines: getAirline(
              applyFiltersExcept('carrier', selections),
              isCustomSearch
            ),
          },
        },
      });

      setFlightResultFiltered(filteredFlights);
      setIsFiltering(false);
    },
    [screenSize.width, state.selections]
  );

  const carrierHandleChange = useCallback(
    code => {
      let carrier = [];

      if (code === 'allOptions') {
        carrier = ['allOptions'];
      } else if (state.selections.carrier.includes(code)) {
        carrier = state.selections.carrier.filter(c => c !== code);
      } else {
        carrier = [
          ...state.selections.carrier.filter(c => c !== 'allOptions'),
          code,
        ];
      }

      if (carrier.length === 0) {
        carrier = ['allOptions'];
      }

      applyFilters({ ...state.selections, carrier });
    },
    [state.selections]
  );

  const baggageHandleChange = useCallback(
    event => {
      const value =
        event.target.value === state.selections.baggage
          ? 'backpack_baggage'
          : event.target.value;

      applyFilters({ ...state.selections, baggage: value });
    },
    [state.selections]
  );

  const scalesHandleChange = useCallback(
    event => {
      const value =
        event.target.value === state.selections.scale
          ? 'allOptions'
          : event.target.value;

      applyFilters({ ...state.selections, scale: value });
    },
    [state.selections]
  );

  const handleChangePrice = useCallback(
    (_, value) => applyFilters({ ...state.selections, price: value }),
    [state.selections]
  );

  const handleChangeDuration = useCallback(
    (_, value) => applyFilters({ ...state.selections, duration: value }),
    [state.selections]
  );

  const handleClearFilters = useCallback(() => {
    setIsFiltering(true);

    const newInitialFlights = isCustomSearch
      ? optimizedCustomClusters
      : optimizedClusters;

    const newState = {
      selections: {
        baggage: 'backpack_baggage',
        scale: 'allOptions',
        carrier: ['allOptions'],
        price: initialFilters.max_price,
        duration: initialFilters.max_duration,
      },
      ranges: {
        price: [initialFilters.min_price, initialFilters.max_price],
        duration: [initialFilters.min_duration, initialFilters.max_duration],
      },
      available: {
        baggage: initialFilters.baggage,
        scales: initialFilters.scales,
        airlines: getAirline(newInitialFlights),
      },
      filteredFlights: newInitialFlights,
      isLoading: false,
    };

    dispatch({ type: 'CLEAR', payload: newState });
    setFlightResultFiltered(newInitialFlights);
    setNoResultsInTheFilter(false);
    setIsFiltering(false);
  }, [
    isCustomSearch,
    optimizedClusters,
    optimizedCustomClusters,
    initialFilters,
    getAirline,
  ]);

  useEffect(() => {
    setFlightResultFiltered(state.filteredFlights);
  }, [state.filteredFlights]);

  return {
    airlineSelected: state.selections.carrier,
    allAirline: state.available.airlines,
    baggageSelected: state.selections.baggage,
    baggage: state.available.baggage,
    scaleSelected: state.selections.scale,
    scales: state.available.scales,
    priceSelected: state.selections.price,
    durationTimeSelected: state.selections.duration,
    minAndMaxDuration: state.ranges.duration,
    minAndMaxPriceRange: state.ranges.price,
    carrierHandleChange,
    baggageHandleChange,
    scalesHandleChange,
    handleChangePrice,
    handleChangeDuration,
    handleClearFilters,
    isLoading: state.isLoading,
  };
};

export default useFlightFilters;
